import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

/**
 * Gate a tree behind authentication.
 *  - While the AuthContext is bootstrapping (verifying a stored JWT) we show
 *    a thin centred placeholder.
 *  - If there is no authenticated user we redirect to /login, preserving the
 *    intended path in router state so post-login bounce-back can use it.
 *  - If the authenticated user has `must_change_password=true` we force them
 *    to /account/change-password unless they're already there.
 *  - If `requireAdmin` is set and the user is not admin we send them to /403.
 */
export default function RequireAuth({ children, requireAdmin = false }) {
    const { token, user, bootstrapping, mustChangePassword, isAdmin } = useAuth();
    const location = useLocation();

    if (bootstrapping) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-ux-bg-0 text-ux-text-3"
                data-testid="auth-verifying"
            >
                Verifying session…
            </div>
        );
    }

    if (!token || !user) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    if (
        mustChangePassword
        && location.pathname !== "/account/change-password"
    ) {
        return <Navigate to="/account/change-password" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/403" replace />;
    }

    return children;
}
